"use client";

import React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Flex,
  HStack,
  Button,
  useToast,
  Select,
  Tabs,
  TabList,
  Tab,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Text,
  useDisclosure,
  Spinner,
  Card,
  CardBody,
  Badge,
  IconButton,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { Trash2, Mail, LinkIcon } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useStableCredits } from "../../hooks/useStableCredits";
import EmailAgents from "../EmailAgents";
import EmailKnowledgeBase from "./EmailKnowledgeBase";
import { EmailAddressDetails } from "./EmailAddressDetails";
import { SendInitialEmailDialog } from "../email-addresses/SendInitialEmailDialog";
import {
  emailAddressService,
  type EmailAddress,
} from "../../email-services/emailAddressService";
import {
  emailAgentService,
  type EmailAgent,
} from "../../email-services/agentService";

const MotionBox = motion(Box);

const CACHE_TTL_MS = 5 * 60 * 1000;

type SendEmailIconProps = {
  size?: number;
  color?: string;
  strokeWidth?: number;
  background?: string;
  opacity?: number;
  rotation?: number;
  shadow?: number;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
  padding?: number;
};

const SendEmailIcon: React.FC<SendEmailIconProps> = ({
  size = 16,
  color = "currentColor",
  strokeWidth = 2,
  background = "transparent",
  opacity = 1,
  rotation = 0,
  shadow = 0,
  flipHorizontal = false,
  flipVertical = false,
  padding = 0,
}) => {
  const transforms: string[] = [];
  if (rotation !== 0) transforms.push(`rotate(${rotation}deg)`);
  if (flipHorizontal) transforms.push("scaleX(-1)");
  if (flipVertical) transforms.push("scaleY(-1)");

  const base = 48;
  const viewBoxSize = base + padding * 2;
  const viewBoxOffset = -padding;
  const viewBox = `${viewBoxOffset} ${viewBoxOffset} ${viewBoxSize} ${viewBoxSize}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        opacity,
        transform: transforms.join(" ") || undefined,
        filter:
          shadow > 0
            ? `drop-shadow(0 ${shadow}px ${shadow * 2}px rgba(0,0,0,0.3))`
            : undefined,
        backgroundColor: background !== "transparent" ? background : undefined,
      }}
    >
      <g fill="none">
        <path d="M44 24V9H24H4V24V39H24" />
        <path d="M44 34L30 34" />
        <path d="M39 29L44 34L39 39" />
        <path d="M4 9L24 24L44 9" />
      </g>
    </svg>
  );
};

const EmailAddresses = () => {
  const { isAdmin, currentClientId, clients, user, userProfile } = useAuth();
  const credits = useStableCredits();
  const location = useLocation();

  const [selectedEmailTab, setSelectedEmailTab] =
    useState<string>("phone-numbers");
  const [selectedAgentType, setSelectedAgentType] = useState<string>(
    () => localStorage.getItem("currentAgentType") || "Email"
  );

  const [emailAddresses, setEmailAddresses] = useState<EmailAddress[]>([]);
  const [emailAgents, setEmailAgents] = useState<EmailAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedEmailAddress, setSelectedEmailAddress] =
    useState<EmailAddress | null>(null);
  const [emailAddressToDelete, setEmailAddressToDelete] =
    useState<EmailAddress | null>(null);
  const [selectedEmailForSending, setSelectedEmailForSending] =
    useState<EmailAddress | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure(); // (kept to avoid breaking existing behavior)
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure();
  const {
    isOpen: isSendEmailDialogOpen,
    onOpen: onSendEmailDialogOpen,
    onClose: onSendEmailDialogClose,
  } = useDisclosure();

  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const navigate = useNavigate();

  // ---- abort support (tenant-safe + prevents race conditions) ----
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // ---- company scoping (stable, tenant-aware) ----
  const companyName = React.useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient
      ? currentClient.company_name
      : userProfile?.company_name;
  }, [isAdmin, currentClientId, clients, userProfile]);

  // ---- cache helpers (tenant-scoped) ----
  const getCacheKey = React.useCallback(
    (key: string) => `email_addresses_${key}_${companyName || "self"}`,
    [companyName]
  );

  const loadFromCache = React.useCallback(
    (key: string) => {
      try {
        const raw = localStorage.getItem(getCacheKey(key));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed.timestamp || 0);
        if (age > CACHE_TTL_MS) return null;
        return parsed.data ?? null;
      } catch {
        return null;
      }
    },
    [getCacheKey]
  );

  const saveToCache = React.useCallback(
    (key: string, data: any) => {
      try {
        localStorage.setItem(
          getCacheKey(key),
          JSON.stringify({ data, timestamp: Date.now() })
        );
      } catch {}
    },
    [getCacheKey]
  );

  // ---- tab state sync (unchanged) ----
  useEffect(() => {
    const savedTab = localStorage.getItem("currentAgentTab") || "phone-numbers";
    setSelectedEmailTab(savedTab);

    const handleTabChange = (event: any) => {
      setSelectedEmailTab(event.detail);
      localStorage.setItem("currentAgentTab", event.detail);
    };

    window.addEventListener("agentTabChange", handleTabChange);
    return () => window.removeEventListener("agentTabChange", handleTabChange);
  }, []);

  // ---- tenant switch behavior: abort + clear stale UI + decide loader based on cache ----
  React.useEffect(() => {
    // abort any in-flight requests from previous tenant immediately
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear previous tenant's UI so you don't flash wrong data when switching companies
    setEmailAddresses([]);
    setEmailAgents([]);
    setIsLoading(true);

    // Only show the big loader if we have nothing cached for this tenant
    const hasCachedAddresses = !!loadFromCache("addresses");
    const hasCachedAgents = !!loadFromCache("agents");
    setIsLoading(!(hasCachedAddresses || hasCachedAgents));
  }, [companyName, loadFromCache]);

  // ---- cache-first, abortable load ----
  const loadData = React.useCallback(async () => {
    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // 1) Hydrate from cache immediately for instant UI
    const cachedAddresses = loadFromCache("addresses");
    const cachedAgents = loadFromCache("agents");

    if (cachedAddresses) setEmailAddresses(cachedAddresses);
    if (cachedAgents) setEmailAgents(cachedAgents);

    try {
      if (!companyName) {
        if (!signal.aborted) {
          setEmailAddresses([]);
          setEmailAgents([]);
          setIsLoading(false);
        }
        return;
      }

      // 2) Fetch fresh core data in parallel
      const [addresses, agents] = await Promise.all([
        // Services may not accept AbortSignal; still guarded by signal checks.
        emailAddressService.list(companyName),
        emailAgentService.list(companyName),
      ]);

      if (signal.aborted) return;

      setEmailAddresses(addresses);
      saveToCache("addresses", addresses);

      setEmailAgents(agents);
      saveToCache("agents", agents);
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("Error loading data:", error);
      toast({ title: "Error loading data", status: "error", duration: 3000 });
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [companyName, loadFromCache, saveToCache, toast]);

  // ---- initial + reactive loads (replaces prior duplicated effects; preserves behavior) ----
  React.useEffect(() => {
    if (userProfile || (isAdmin && currentClientId)) {
      loadData();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [userProfile, isAdmin, currentClientId, loadData]);

  // ---- handle OAuth redirect success (preserved) ----
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("connected") === "1") {
      toast({
        title: "Gmail Connected!",
        description: "Your Gmail account has been successfully connected.",
        status: "success",
        duration: 5000,
      });
      // Clear the query param
      navigate(location.pathname, { replace: true });
      loadData();
    }
  }, [location.search, loadData, navigate, location.pathname, toast]);

  const handleTabChange = (newTab: string) => {
    setSelectedEmailTab(newTab);
    localStorage.setItem("currentAgentTab", newTab);
    window.dispatchEvent(new CustomEvent("agentTabChange", { detail: newTab }));
  };

  const handleAddEmailAddress = () => {
    const userId = user?.id;

    if (!userId) {
      toast({
        title: "Error",
        description: "User authentication required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Use memoized companyName (same behavior, less duplication)
    if (!companyName) {
      toast({
        title: "Error",
        description: "No company selected",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Redirect to OAuth flow with company name
    const serverUrl =
      process.env.REACT_APP_SERVER_URL || "http://localhost:8000";
    window.location.href = `${serverUrl}/api/google/oauth/start?userId=${userId}&companyName=${encodeURIComponent(
      companyName
    )}`;
  };

  const handleEmailAddressClick = (address: EmailAddress) => {
    setSelectedEmailAddress(address);
    onEditModalOpen();
  };

  const handleDeleteClick = (address: EmailAddress, e: React.MouseEvent) => {
    e.stopPropagation();
    setEmailAddressToDelete(address);
    onDeleteAlertOpen();
  };

  const handleSendEmailClick = (
    emailAddress: EmailAddress,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setSelectedEmailForSending(emailAddress);
    onSendEmailDialogOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!emailAddressToDelete) return;

    try {
      await emailAddressService.delete(emailAddressToDelete.id);
      toast({
        title: "Email address deleted",
        status: "success",
        duration: 3000,
      });

      // Refresh data (cache will also update)
      loadData();

      onDeleteAlertClose();
      setEmailAddressToDelete(null);
    } catch (error) {
      toast({
        title: "Error deleting email address",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);
    if (type === "All") {
      navigate("/all-agents");
    } else if (type === "Voice") {
      navigate("/voice-agents");
    } else if (type === "SMS") {
      navigate("/sms-agents");
    } else if (type === "Email") {
      navigate("/email-agents");
    } else if (type === "WhatsApp") {
      navigate("/whatsapp-agents");
    }
  };

  const getTabIndex = () => {
    switch (selectedEmailTab) {
      case "agents":
        return 0;
      case "phone-numbers":
        return 1;
      case "knowledge-base":
        return 2;
      default:
        return 1;
    }
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = ["agents", "phone-numbers", "knowledge-base"];
    handleTabChange(tabs[index]);
  };

  const getAgentName = (agentId: string | null | undefined) => {
    if (!agentId) return "None";
    const agent = emailAgents.find((a) => a.agent_id === agentId);
    return agent?.name || agentId;
  };

  const canSendEmail = (emailAddress: EmailAddress) => {
    return emailAddress.is_connected && emailAddress.assigned_agent_id;
  };

  const handleEmailAddressChange = () => {
    loadData();
    onEditModalClose();
  };

  if (selectedAgentType === "Email") {
    if (selectedEmailTab === "agents") {
      return <EmailAgents />;
    }
    if (selectedEmailTab === "knowledge-base") {
      return <EmailKnowledgeBase />;
    }
  }

  const totalAddresses = emailAddresses.length;
  const assignedAddresses = emailAddresses.filter(
    (addr) => addr.assigned_agent_id
  ).length;
  const unassignedAddresses = totalAddresses - assignedAddresses;

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Tabs
            index={getTabIndex()}
            onChange={handleTabIndexChange}
            variant="solid-rounded"
            colorScheme="blackAlpha"
          >
            <TabList
              bg="gray.100"
              p={1}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.300"
            >
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg: selectedEmailTab === "agents" ? "white" : "gray.200",
                }}
              >
                📧 Email Agents
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg:
                    selectedEmailTab === "phone-numbers" ? "white" : "gray.200",
                }}
              >
                📬 Email Addresses
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg:
                    selectedEmailTab === "knowledge-base"
                      ? "white"
                      : "gray.200",
                }}
              >
                📚 Knowledge Base
              </Tab>
            </TabList>
          </Tabs>
        </HStack>
        <HStack spacing={2}>
          <MotionBox
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            transition="0.15s ease"
          >
            <Select
              value={selectedAgentType}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              height="48px"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="12px"
              color="gray.800"
              fontWeight="semibold"
              fontSize="sm"
              pl="12px"
              pr="36px"
              cursor="pointer"
              shadow="sm"
              transition="all 0.2s ease"
              _hover={{
                borderColor: "primary.400",
                shadow: "md",
              }}
              _focus={{
                borderColor: "primary.600",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
              }}
            >
              <option value="All">🤖 All Agents</option>
              <option value="Voice">📞 Voice Agents</option>
              <option value="SMS">💬 SMS Agents</option>

              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <option value="Email">📧 Email Agents</option>
              )}

              <option value="WhatsApp">📱 WhatsApp Agents</option>
            </Select>
          </MotionBox>
          <Button
            onClick={handleAddEmailAddress}
            variant="brandGradient"
            fontWeight="medium"
            leftIcon={<LinkIcon size={16} />}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to connect Gmail"
                : "Connect your Gmail account"
            }
          >
            Connect Gmail
          </Button>
        </HStack>
      </Flex>

      {!isLoading && emailAddresses.length > 0 && (
        <Grid
          templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
          gap={4}
          mb={6}
        >
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Addresses</StatLabel>
                <StatNumber color="black">{totalAddresses}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Assigned to Agents</StatLabel>
                <StatNumber color="black">{assignedAddresses}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Unassigned</StatLabel>
                <StatNumber color="black">{unassignedAddresses}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>
      )}

      {isLoading ? (
        <Flex
          justify="center"
          align="center"
          h="500px"
          minH="calc(100vh - 80px)"
        >
          <Spinner size="xl" color="orange.500" />
        </Flex>
      ) : emailAddresses.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="500px"
          minH="calc(100vh - 80px)"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <Mail size={48} color="gray" />
          <Text fontSize="lg" fontWeight="medium" color="gray.600" mt={4}>
            No Gmail accounts connected yet
          </Text>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Connect your Gmail account to start receiving and managing emails
          </Text>
          <Button
            onClick={handleAddEmailAddress}
            colorScheme="primary"
            bg="primary.800"
            _hover={{ bg: "primary.900" }}
            leftIcon={<LinkIcon size={16} />}
            mt={6}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to connect Gmail"
                : "Connect your Gmail account"
            }
          >
            Connect Gmail
          </Button>
        </Flex>
      ) : (
        <Grid templateColumns="repeat(auto-fill, minmax(400px, 1fr))" gap={6}>
          {emailAddresses.map((addr) => (
            <Card
              role="button"
              tabIndex={credits !== null && credits < 0.01 ? -1 : 0}
              aria-disabled={credits !== null && credits < 0.01}
              key={addr.id}
              borderLeft="4px"
              borderLeftColor="orange.500"
              cursor={
                credits !== null && credits < 0.01 ? "not-allowed" : "pointer"
              }
              onClick={() => {
                if (credits !== null && credits < 0.01) return;
                handleEmailAddressClick(addr);
              }}
              _hover={credits !== null && credits < 0.01 ? {} : { shadow: "md" }}
              height="250px"
              display="flex"
              flexDirection="column"
              opacity={credits !== null && credits < 0.01 ? 0.5 : 1}
              transition="all 0.2s"
              title={
                credits !== null && credits < 0.01
                  ? "Insufficient balance to view email address details"
                  : undefined
              }
            >
              <CardBody display="flex" flexDirection="column" p={5}>
                <VStack align="stretch" spacing={3} flex={1}>
                  {/* Header */}
                  <Flex justify="space-between" align="start">
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="lg" fontWeight="bold" color="black">
                        {addr.email_address}
                      </Text>
                      <Badge
                        colorScheme={addr.is_connected ? "green" : "gray"}
                        fontSize="xs"
                      >
                        {addr.is_connected ? "Connected" : "Disconnected"}
                      </Badge>
                    </VStack>
                    <IconButton
                      aria-label="Send email"
                      icon={<SendEmailIcon size={16} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={(e) => handleSendEmailClick(addr, e)}
                      isDisabled={!canSendEmail(addr)}
                    />

                    <IconButton
                      aria-label="Delete"
                      icon={<Trash2 size={16} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={(e) => handleDeleteClick(addr, e)}
                    />
                  </Flex>

                  <VStack
                    align="stretch"
                    spacing={2}
                    pt={2}
                    borderTop="1px solid"
                    borderColor="gray.100"
                  >
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Agent:
                      </Text>
                      <Badge
                        colorScheme={addr.assigned_agent_id ? "green" : "gray"}
                        fontSize="xs"
                      >
                        {getAgentName(addr.assigned_agent_id)}
                      </Badge>
                    </Flex>
                  </VStack>

                  {addr.oauth_provider && (
                    <Text
                      fontSize="xs"
                      color="gray.400"
                      pt={2}
                      borderTop="1px solid"
                      borderColor="gray.100"
                      mt="auto"
                    >
                      Provider:{" "}
                      {addr.oauth_provider.charAt(0).toUpperCase() +
                        addr.oauth_provider.slice(1)}{" "}
                      (OAuth)
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}

      {/* Email Address Details Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Email Address Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedEmailAddress && (
              <EmailAddressDetails
                emailAddress={selectedEmailAddress}
                agents={emailAgents.map((a) => ({
                  id: a.agent_id,
                  name: a.name,
                }))}
                onUpdate={handleEmailAddressChange}
                onDelete={() => {
                  setSelectedEmailAddress(null);
                  onEditModalClose();
                  loadData();
                }}
                onSendEmail={() => {
                  if (selectedEmailAddress) {
                    setSelectedEmailForSending(selectedEmailAddress);
                    onSendEmailDialogOpen();
                  }
                }}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Email Address
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete{" "}
              <strong>{emailAddressToDelete?.email_address}</strong>? This
              action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                variant="ghost"
                color="black"
                _hover={{ bg: "gray.100" }}
                ref={cancelRef}
                onClick={onDeleteAlertClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                bg="red.500"
                _hover={{ bg: "red.600" }}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Send Initial Email Dialog */}
      {selectedEmailForSending && (
        <SendInitialEmailDialog
          isOpen={isSendEmailDialogOpen}
          onClose={onSendEmailDialogClose}
          agentId={selectedEmailForSending.assigned_agent_id || ""}
          fromEmail={selectedEmailForSending.email_address}
          initialEmailSubject={
            selectedEmailForSending.assigned_agent_id
              ? emailAgents.find(
                  (a) =>
                    a.agent_id === selectedEmailForSending.assigned_agent_id
                )?.initial_email_subject ?? ""
              : ""
          }
          initialEmailContent={
            selectedEmailForSending.assigned_agent_id
              ? emailAgents.find(
                  (a) =>
                    a.agent_id === selectedEmailForSending.assigned_agent_id
                )?.initial_email_content ?? ""
              : ""
          }
        />
      )}
    </Box>
  );
};

export default EmailAddresses;
